import { isMembershipItem } from "common";
import { snakeCase } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation } from "react-query";
import { FieldVisibility, Form, FormSource, PreferenceKey, ProductObject, PurchaseItem } from "types";
import { isAdditionalInformationQuestionsExistInForm } from "../../../../packages/common/src/models/PurchaseItem";
import { api } from "../api/api";
import { getProductForm } from "../models/Form/Form";
import { useAutoFetchCollection } from "./useAutoFetchCollection";
import { useInfiniteCollection } from "./useInfiniteCollection";
import { useInvalidate } from "@/hooks/useInvalidation";
import { useOptimisticUpdate } from "@/hooks/useOptimisticUpdate";
import { findByKey } from "@/models/Preference";
import { DashboardRosterPreference, RosterViewConfiguration } from "@/types/Dashboard";

export const useDeleteForm = (form: Form | undefined) => {
    return useMutation(async () => {
        if (!form?.id) {
            return null;
        }

        return api.delete(`/forms/${form.id}`);
    });
};

export const useSaveForm = () => {
    return useMutation(async (payload: any) => {
        if (payload.id) {
            return api.put(`/forms/${payload.id}`, payload);
        }

        return api.post("/forms", payload);
    });
};

export const useForm = (productId?: string, productType?: string) => {
    const [form, setForm] = useState<Form>();
    const { data: forms, isLoading } = useAutoFetchCollection<Form>(["forms"], {
        productType,
    });

    const [isGlobalFormExists, setGlobalFormExists] = useState(false);

    const initialForm = getProductForm(forms, productId);
    const globalForm = isGlobalFormExists ? forms.find((form) => !form?.product) : undefined;

    useEffect(() => {
        if (forms) {
            const form = getProductForm(forms, productId);
            setForm(form);

            setGlobalFormExists(productId ? forms.some((form) => !form.product) : !!form);
        }
    }, [forms, productId]);

    return useMemo(
        () => ({ form, setForm, isLoading, isGlobalFormExists, initialForm, globalForm }),
        [form, setForm, isLoading, isGlobalFormExists, initialForm, globalForm],
    );
};

export const usePurchaseFormConfig = (purchaseItem: PurchaseItem) => {
    const { form: experienceFormConfig, globalForm: experienceGlobalForm } = useForm(purchaseItem.product.id);
    const { form: membershipFormConfig, globalForm: membershipGlobalForm } = useForm(
        purchaseItem.product.id,
        ProductObject.MEMBERSHIP,
    );

    const isItemLevelFormPurchase = !!purchaseItem.form?.organizerOnly;
    const isItemLevelFormExperienceConfig =
        experienceFormConfig?.organizerOnly ?? !!experienceGlobalForm?.organizerOnly;
    const isItemLevelFormMembershipConfig =
        membershipFormConfig?.organizerOnly ?? !!membershipGlobalForm?.organizerOnly;

    const hasItemLevelResponse = !!purchaseItem.formResponse;
    const hasGuestLevelResponse = purchaseItem.guests.some((guest) => guest.formResponse);

    // Checks if both experience and purchase has item level config.
    const isItemLevelFormAndConfig =
        isAdditionalInformationQuestionsExistInForm(experienceFormConfig ?? experienceGlobalForm) ||
        (isItemLevelFormPurchase &&
            (isItemLevelFormExperienceConfig || (isMembershipItem(purchaseItem) && isItemLevelFormMembershipConfig)));

    // Checks if questionnaire is configured for item level or already has a response.
    const hasOrganizerConfigOrResponse =
        isItemLevelFormPurchase ||
        isItemLevelFormAndConfig ||
        isItemLevelFormExperienceConfig ||
        isItemLevelFormMembershipConfig ||
        hasItemLevelResponse;

    // Checks if questionnaire is configured for guest level or already has a response for any of guests.
    const hasGuestConfigOrResponse =
        !isItemLevelFormPurchase ||
        (isMembershipItem(purchaseItem) && !isItemLevelFormMembershipConfig) ||
        (!isMembershipItem(purchaseItem) && !isItemLevelFormExperienceConfig) ||
        hasGuestLevelResponse;

    return {
        isItemLevelFormAndConfig,
        hasOrganizerConfigOrResponse,
        hasGuestConfigOrResponse,
    };
};

/**
 * Saving questionnaire in all configs for fields where addToSavedViews and roster are true
 */
export const useQuestionnairePreference = (
    defaultViewConfiguration: null | [] = null,
    onSave?: (preference: DashboardRosterPreference) => void,
) => {
    const invalidate = useInvalidate();
    const { data: preferences = [], isLoading } = useInfiniteCollection<DashboardRosterPreference>("preferences");

    const { isLoading: isSaving, mutate: savePreference } = useMutation(
        "preferences",
        async (preference: DashboardRosterPreference) => {
            const { data: result } = preference.id
                ? await api.patch<DashboardRosterPreference>(`/preferences/${preference.id}`, preference)
                : await api.post<DashboardRosterPreference>("/preferences", preference);

            invalidate("preferences", result.id);
            onSave?.(result);
        },
    );

    const rosterKey = PreferenceKey.XOLA_SELLER_ROSTER;
    const dashboardRosterPreferences = findByKey(preferences, rosterKey) as DashboardRosterPreference;
    const [preference, setPreference] = useOptimisticUpdate(dashboardRosterPreferences, isSaving, (preference) => {
        savePreference(preference);
    });

    const viewConfigurations = dashboardRosterPreferences?.value?.configurations ?? defaultViewConfiguration;
    return { preference, viewConfigurations, isLoading, setPreference, savePreference, isSaving };
};

export const getVisibleFields = (form: Form, source: FormSource) => {
    return form.fields.map((field) => {
        const visibility = source === FormSource.PLUGIN ? field.visibility : FieldVisibility.ALL;
        return {
            ...field,
            visibility,
            organizerOnly: source === FormSource.PLUGIN ? false : field.organizerOnly,
        };
    });
};

export const getOptionalSourcesList = (form: Form, value: boolean) => {
    const optionalSourcesList = [...(form.optionalSourcesList ?? []), FormSource.XOLA];
    return value ? optionalSourcesList : optionalSourcesList.filter((source) => source !== FormSource.XOLA);
};

export const XOLA_DEFAULT_CONFIG_NAME = "xola_default";
export const getSavedViewsForUpdate = (form: Form, configurations: RosterViewConfiguration[], productId: string) => {
    let localViewConfigurations = configurations;
    const savedViewFields = form.fields.filter((field) => field.addToSavedViews);
    for (const field of savedViewFields) {
        localViewConfigurations = localViewConfigurations.map((viewConfiguration) => {
            const newQuestionnaireColumn = {
                hide: false,
                id: snakeCase(field.label),
                pinned: null,
                sort: null,
                productId,
            };

            // viewConfiguration doesn't have columns if it's XOLA_DEFAULT config
            const isQuestionnaireExistInConfig = viewConfiguration?.columns?.find((column) => {
                return column.id === snakeCase(field.label);
            });

            if (viewConfiguration.id === XOLA_DEFAULT_CONFIG_NAME || isQuestionnaireExistInConfig) {
                return viewConfiguration;
            }

            return {
                ...viewConfiguration,
                columns: [...viewConfiguration.columns, { ...newQuestionnaireColumn }],
            };
        });
    }

    return localViewConfigurations.map((localViewConfiguration) => {
        if (localViewConfiguration.id === XOLA_DEFAULT_CONFIG_NAME) {
            return localViewConfiguration;
        }

        const filteredColumns = localViewConfiguration.columns.filter((column) => {
            if (!column?.productId) {
                return true;
            }

            if (column.productId !== productId) {
                return true;
            }

            if (column.productId === productId) {
                // if true the field still exists
                return form.fields.find((field) => snakeCase(field.label) === column.id);
            }

            return true;
        });

        return {
            ...localViewConfiguration,
            columns: filteredColumns,
        };
    });
};

export const useSaveQuestionnaireForSavedViews = () => {
    const { viewConfigurations, setPreference } = useQuestionnairePreference();

    return useCallback(
        async (form: Form, productId: string) => {
            if (!viewConfigurations) {
                return;
            }

            const preferencesToUpdate = getSavedViewsForUpdate(form, viewConfigurations, productId);
            setPreference((preference) => {
                preference.value.configurations = [...preferencesToUpdate];
            });
        },
        [viewConfigurations, setPreference],
    );
};
